import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartState, CartItem } from '../types';
import { getLotSize } from '../utils/lotSize';

const initialState: CartState = {
  items: [],
  total: 0
};

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => {
    const lotSize = getLotSize(item.product.price, item.product.lotSize);
    return total + (item.product.price * item.quantity * lotSize);
  }, 0);
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(item => item.product._id === action.payload.product._id);
      const requestedQuantity = action.payload.quantity;
      const currentQuantity = existingItem ? existingItem.quantity : 0;
      const totalRequested = currentQuantity + requestedQuantity;

      const lotSize = getLotSize(action.payload.product.price, action.payload.product.lotSize);
      const stockCheck = totalRequested * lotSize <= action.payload.product.stock;

      if (!stockCheck) {
        throw new Error(`Stock insuffisant. Disponible: ${action.payload.product.stock} pièces`);
      }

      if (existingItem) {
        existingItem.quantity += requestedQuantity;
      } else {
        state.items.push(action.payload);
      }

      state.total = calculateTotal(state.items);
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.product._id !== action.payload);
      state.total = calculateTotal(state.items);
    },

    updateQuantity: (state, action: PayloadAction<{ productId: string; quantity: number }>) => {
      const item = state.items.find(i => i.product._id === action.payload.productId);
      if (item) {
        const requestedQuantity = action.payload.quantity;
        const lotSize = getLotSize(item.product.price, item.product.lotSize);

        const stockCheck = requestedQuantity * lotSize <= item.product.stock;
        if (!stockCheck) {
          throw new Error(`Stock insuffisant. Disponible: ${item.product.stock} pièces`);
        }

        item.quantity = requestedQuantity;
        state.total = calculateTotal(state.items);
      }
    },
    incrementQuantity: (state, action) => {
      const productId = action.payload;
      const item = state.items.find(i => i.product._id === productId);
      if (item) {
        item.quantity += 1;
      }
      state.total = calculateTotal(state.items);
    },

    decrementQuantity: (state, action) => {
      const productId = action.payload;
      const item = state.items.find(i => i.product._id === productId);
      if (item && item.quantity > 1) {
        item.quantity -= 1;
      }
      state.total = calculateTotal(state.items);
    },

    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    }
  }
});

export const { addToCart, removeFromCart, updateQuantity, clearCart, incrementQuantity, decrementQuantity } = cartSlice.actions;
export default cartSlice.reducer;
